import React from 'react';
import { Drawer, Button, Switch, Typography } from 'antd';
import { Controller } from 'react-hook-form';
import { useTheme } from '../../../../context/ThemeContext';
import {
  StyledInput,
  FullWidthSpaceLarge,
  CreateButtonPrimary,
  HelperTextContainer,
  SecondaryTextSmall,
  InputNumberFullWidth,
  SubCard,
} from '../Styles';
import { CreateCustomizationDrawerProps } from '../types';
import { useCreateCustomizationForm } from '../hooks/useCreateCustomizationForm';

const { Text } = Typography;

const CreateCustomizationDrawer: React.FC<CreateCustomizationDrawerProps> = ({ open, onClose, onSubmit }) => {
  const { isDarkMode } = useTheme();
  const { control, handleSubmit, trigger, errors, isSubmitting, quantityEnabled, multipleSelections, onFormSubmit } =
    useCreateCustomizationForm(open, onSubmit);

  const labelColor = isDarkMode ? '#e5e7eb' : undefined;

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Button onClick={onClose} disabled={isSubmitting} style={{ height: 40, paddingInline: 20 }}>
        Cancel
      </Button>
      <CreateButtonPrimary
        onClick={handleSubmit(onFormSubmit)}
        loading={isSubmitting}
        disabled={isSubmitting}
        style={{ height: 40, paddingInline: 24 }}
      >
        Create Customization
      </CreateButtonPrimary>
    </div>
  );

  return (
    <Drawer title="Create New Customization" placement="right" width={700} onClose={onClose} open={open} destroyOnClose maskClosable={false} footer={footer}>
      <FullWidthSpaceLarge direction="vertical" size="large">

        {/* Name */}
        <div>
          <Text strong>Customization Name <Text type="danger">*</Text></Text>
          <HelperTextContainer>
            <SecondaryTextSmall type="secondary">Give this customization a clear name</SecondaryTextSmall>
          </HelperTextContainer>
          <Controller name="customization" control={control} render={({ field }) => (
            <StyledInput {...field} placeholder="Enter customization name" status={errors.customization ? 'error' : ''} />
          )} />
          {errors.customization && <Text type="danger" style={{ fontSize: 12 }}>{errors.customization.message}</Text>}
        </div>

        {/* Note */}
        <div>
          <Text strong>Note</Text>
          <HelperTextContainer>
            <SecondaryTextSmall type="secondary">Add any additional notes or instructions</SecondaryTextSmall>
          </HelperTextContainer>
          <Controller name="note" control={control} render={({ field }) => (
            <StyledInput {...field} placeholder="Add a description" />
          )} />
        </div>

        {/* Quantity per option */}
        <div>
          <Text strong>Quantity per option</Text>
          <HelperTextContainer>
            <SecondaryTextSmall type="secondary">
              Limit how many units of a single option a customer can add (e.g. max 3 servings of fries)
            </SecondaryTextSmall>
          </HelperTextContainer>
          <Controller name="quantityEnabled" control={control} render={({ field }) => (
            <Switch checked={field.value} onChange={field.onChange} />
          )} />
          {quantityEnabled && (
            <SubCard $isDark={isDarkMode}>
              <div>
                <Text strong style={{ color: labelColor }}>Minimum quantity <Text type="danger">*</Text></Text>
                <HelperTextContainer>
                  <SecondaryTextSmall type="secondary">Minimum units of a single option the customer must add</SecondaryTextSmall>
                </HelperTextContainer>
                <Controller name="minQuantity" control={control} render={({ field }) => (
                  <InputNumberFullWidth value={field.value} placeholder="e.g. 1" status={errors.minQuantity ? 'error' : ''} onBlur={field.onBlur}
                    onChange={(value) => { field.onChange(typeof value === 'number' ? value : null); trigger('maxQuantity'); }} />
                )} />
                {errors.minQuantity && <Text type="danger" style={{ fontSize: 12 }}>{errors.minQuantity.message}</Text>}
              </div>
              <div>
                <Text strong style={{ color: labelColor }}>Maximum quantity <Text type="danger">*</Text></Text>
                <HelperTextContainer>
                  <SecondaryTextSmall type="secondary">Maximum units of a single option the customer can add</SecondaryTextSmall>
                </HelperTextContainer>
                <Controller name="maxQuantity" control={control} render={({ field }) => (
                  <InputNumberFullWidth value={field.value} placeholder="e.g. 5" status={errors.maxQuantity ? 'error' : ''} onBlur={field.onBlur}
                    onChange={(value) => { field.onChange(typeof value === 'number' ? value : null); }} />
                )} />
                {errors.maxQuantity && <Text type="danger" style={{ fontSize: 12 }}>{errors.maxQuantity.message}</Text>}
              </div>
            </SubCard>
          )}
        </div>

        {/* Multiple selections */}
        <div>
          <Text strong>Multiple selections</Text>
          <HelperTextContainer>
            <SecondaryTextSmall type="secondary">
              Allow customers to pick more than one option from this list (e.g. choose 3 toppings out of 5)
            </SecondaryTextSmall>
          </HelperTextContainer>
          <Controller name="multipleSelections" control={control} render={({ field }) => (
            <Switch checked={field.value} onChange={field.onChange} />
          )} />
          {multipleSelections && (
            <SubCard $isDark={isDarkMode} style={{ gap: 0 }}>
              <Text strong style={{ color: labelColor }}>
                Maximum options a customer can select <Text type="danger">*</Text>
              </Text>
              <HelperTextContainer>
                <SecondaryTextSmall type="secondary">
                  How many different options can a customer choose from this list? (e.g. pick any 3 out of 5 toppings)
                </SecondaryTextSmall>
              </HelperTextContainer>
              <Controller name="multipleSelectionsQuantity" control={control} render={({ field }) => (
                <InputNumberFullWidth value={field.value} placeholder="e.g. 3" status={errors.multipleSelectionsQuantity ? 'error' : ''} onBlur={field.onBlur}
                  onChange={(value) => { field.onChange(typeof value === 'number' ? value : null); }} />
              )} />
              {errors.multipleSelectionsQuantity && (
                <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>{errors.multipleSelectionsQuantity.message}</Text>
              )}
            </SubCard>
          )}
        </div>

        {/* Sold Out */}
        <div>
          <Text strong>Sold Out</Text>
          <HelperTextContainer>
            <SecondaryTextSmall type="secondary">Mark this customization as unavailable</SecondaryTextSmall>
          </HelperTextContainer>
          <Controller name="soldOut" control={control} render={({ field }) => (
            <Switch checked={field.value} onChange={field.onChange} />
          )} />
        </div>

      </FullWidthSpaceLarge>
    </Drawer>
  );
};

export default CreateCustomizationDrawer;
